import { lazy, ReactNode, Suspense } from "react";
import { Route, Routes } from "react-router";
import { useSettingsActions } from "../../actions/settings-actions";
import { useStore } from "../../store";
import { MenuKey, useCustomerHelpers } from "../../utils/customer-helpers";
import { useUserHelpers } from "../../utils/user-helpers";
import NoConnectedCustomer from "../auth/no-customer";
import NotFound from "../auth/not-found";
import FaqView from "../help/faq";
import ReleaseNotesView from "../help/release-notes";
import DashboardContainer from "../reports/dashboard";
import AppViewLoader from "./app-view-loader";

// Lazy loading of components
const Administration = lazy(() => import("../administration"));
const ExternalOrders = lazy(() => import("../reports/purchase-lists/external-orders-list"));
const ExternalOrderItemsList = lazy(
  () => import("../reports/purchase-lists/purchase-order-items-list")
);
const InternalOrders = lazy(() => import("../reports/purchase-lists/internal-orders-list"));
const InternalOrderItemsList = lazy(() => import("../reports/purchase-lists/internal-items-list"));
const DeliverySchedule = lazy(() => import("../reports/delivery-schedule"));
const InternalDeliverySchedule = lazy(() => import("../reports/internal-delivery-schedule"));
const Shipments = lazy(() => import("../reports/shipment-list/external-shipments"));
const InternalShipments = lazy(() => import("../reports/shipment-list/internal-shipments"));
const SiteSchedule = lazy(() => import("../reports/site-schedule"));
const InventoryNationalWarehouse = lazy(() => import("../reports/inventory/nwh"));
const SerialNumbers = lazy(() => import("../reports/serial-numbers"));
const InternalSerialNumbers = lazy(() => import("../reports/internal-serial-numbers"));
const DeliveryList = lazy(() => import("../reports/delivery-list"));
const DeliveryItemList = lazy(() => import("../reports/delivery-item-list"));
const PurchaseOrderList = lazy(() => import("../reports/purchase-order-list"));
const ShipToParty = lazy(() => import("../reports/ship-to-party/ship-to-party-view"));

const LoadLazy = ({ children }: { children: ReactNode }) => (
  <Suspense fallback={<AppViewLoader />}>{children}</Suspense>
);

interface IDynamicRoute {
  key: string;
  menuKey: MenuKey;
  component: any; //ReactElement | ReactNode;
  path: string;
}

const getDynamicRoutes = (): IDynamicRoute[] => {
  return [
    {
      key: "orders",
      menuKey: MenuKey.PurchaseOrders,
      component: ExternalOrders,
      path: "orders/*"
    },
    {
      key: "order-items-list",
      menuKey: MenuKey.PurchaseOrderItemsList,
      component: ExternalOrderItemsList,
      path: "purchase-order-items"
    },
    {
      key: "internal-orders",
      menuKey: MenuKey.InternalPurchaseOrderList,
      component: InternalOrders,
      path: "internal-orders/*"
    },
    {
      key: "internal-items-list",
      menuKey: MenuKey.InternalPurchaseOrderItemsList,
      component: InternalOrderItemsList,
      path: "internal-items"
    },
    {
      key: "delivery-schedule",
      menuKey: MenuKey.DeliverySchedule,
      component: DeliverySchedule,
      path: "delivery-schedule"
    },
    {
      key: "delivery-list",
      menuKey: MenuKey.DeliveryList,
      component: DeliveryList,
      path: "delivery-list"
    },
    {
      key: "delivery-item-list",
      menuKey: MenuKey.DeliveryItemList,
      component: DeliveryItemList,
      path: "delivery-item-list"
    },
    {
      key: "purchase-order-list",
      menuKey: MenuKey.PurchaseOrderList,
      component: PurchaseOrderList,
      path: "purchase-order-list"
    },
    {
      key: "site-schedule",
      menuKey: MenuKey.SiteSchedule,
      component: SiteSchedule,
      path: "site-schedule"
    },
    {
      key: "internal-delivery-schedule",
      menuKey: MenuKey.InternalDeliverySchedule,
      component: InternalDeliverySchedule,
      path: "internal-delivery-schedule"
    },
    {
      key: "shipment-list",
      menuKey: MenuKey.ShipmentList,
      component: Shipments,
      path: "shipment/*"
    },
    {
      key: "internal-shipment-list",
      menuKey: MenuKey.InternalPoShipmentList,
      component: InternalShipments,
      path: "internal-shipment/*"
    },
    {
      key: "inventory-nwh-single-material",
      menuKey: MenuKey.InventoryNationalWarehouse,
      component: InventoryNationalWarehouse,
      path: "inventory-nwh/:materialId"
    },
    {
      key: "inventory-nwh",
      menuKey: MenuKey.InventoryNationalWarehouse,
      component: InventoryNationalWarehouse,
      path: "inventory-nwh/*"
    },
    {
      key: "serial-numbers",
      menuKey: MenuKey.SerialNumberView,
      component: SerialNumbers,
      path: "serials"
    },
    {
      key: "internal-serial-numbers",
      menuKey: MenuKey.SerialNumberInternalView,
      component: InternalSerialNumbers,
      path: "internal-serials"
    },
    {
      key: "ship-to-party",
      menuKey: MenuKey.ShipToPartyView,
      component: ShipToParty,
      path: "ship-to-party"
    }
  ];
};

/**
 * Routing component containing all routes to be placed within the SystemLayer.
 */
export const ApplicationRoutes = () => {
  const [state] = useStore();
  const { currentCustomer } = useSettingsActions();
  const { isMenuEnabled } = useCustomerHelpers();
  const { userRoleIsSuperUser, userRoleIsAdministrator, userRoleIsTechSupport } = useUserHelpers();

  if (!state.user) return <Routes />;
  else if (!state.userRoles.length || !currentCustomer().features.views.length) {
    return (
      <Routes>
        <Route key="no-customers" path="*" element={<AppViewLoader />} />
      </Routes>
    );
  }
  // If user has no customers
  else if (state.user.customers.length <= 0) {
    return (
      <Routes>
        <Route key="no-customers" path="*" element={<NoConnectedCustomer />} />
      </Routes>
    );
  }

  const routes = [
    <Route key="relnotes" path="help/release-notes" element={<ReleaseNotesView />} />,
    <Route key="faq" path="help/faq" element={<FaqView />} />,
    <Route key="dashboard" path="dashboard" element={<DashboardContainer />} />
  ];

  getDynamicRoutes().forEach(r => {
    if (isMenuEnabled(r.menuKey)) {
      const Component = r.component as any;
      routes.push(
        <Route
          key={r.key}
          path={r.path}
          element={
            <LoadLazy>
              <Component />
            </LoadLazy>
          }
        />
      );
    }
  });

  // Routes restricted by user role.
  if (
    userRoleIsSuperUser(state.user.role) ||
    userRoleIsAdministrator(state.user.role) ||
    userRoleIsTechSupport(state.user.role)
  ) {
    routes.push(
      <Route
        key="administration"
        path="administration/*"
        element={
          <LoadLazy>
            <Administration />
          </LoadLazy>
        }
      />
    );
  }

  return (
    <Routes>
      {routes}
      <Route index key="default" path="/" element={<DashboardContainer />} />,
      <Route key="notfound" path="*" element={<NotFound />} />
    </Routes>
  );
};
